import { Activity, Atom, Droplets, HeartPulse, Leaf, Wind } from "lucide-react"
import { imageBaseURL } from "@/lib/utils"
import type { Feature } from "@/components/sections/FeatureGrid"
import type { CarouselImageItem } from "@/components/sections/CarouselImage"
import type { Testimonial } from "@/components/sections/Testimonials"
import PRODUCTS, { type ProductType } from "@/content/products"

/**
 * Contenido de la página de inicio. Todas las imágenes se resuelven aquí con
 * `imageBaseURL()` (sirven desde `public/images/`, respetando el base path en
 * previews). Para cambiar una imagen, edita solo la ruta correspondiente.
 *
 * Nota: son imágenes reales del proyecto. Si se quieren usar fotos de Pexels,
 * deben descargarse a `public/images/` — el hotlink directo a pexels.com lo
 * bloquea la CSP de producción (`img-src 'self'`).
 */

export const hero = {
	eyebrow: "Bioestimulación celular",
	title: "Tratamientos de bioestimulación celular",
	subtitle: "Productos que alivian y previenen padecimientos crónico-degenerativos en todas las edades, de forma natural.",
	primaryCta: { label: "Ver tratamientos", to: "/tratamientos" },
	secondaryCta: { label: "Leer más", scrollToNext: true as const }
}

export const benefits = {
	title: (
		<>
			La ingesta diaria de nuestros productos reactiva las funciones básicas de las células, protegiendo y rehabilitando al
			organismo de forma natural, <span className="text-destructive">sin efectos secundarios</span>.
		</>
	),
	items: [
		{
			icon: Atom,
			title: "Reactivación celular",
			text: "Despiertan la actividad natural de tus células para que el cuerpo se repare desde dentro. Notarás más vitalidad y mejor ánimo en las primeras semanas de uso constante."
		},
		{
			icon: Wind,
			title: "Más energía y oxígeno",
			text: "Mejoran la oxigenación de la sangre y los tejidos, así rindes más durante el día, te concentras mejor y te recuperas más rápido del cansancio y el estrés."
		},
		{
			icon: Droplets,
			title: "Pureza que puedes sentir",
			text: "Fórmulas limpias y transparentes, sin químicos agresivos ni aditivos. Lo que tomas es exactamente lo que tu cuerpo necesita, ni más ni menos."
		},
		{
			icon: Leaf,
			title: "Sin efectos secundarios",
			text: "No son fármacos: trabajan en armonía con tu organismo. Puedes tomarlos a diario, por tiempo prolongado y junto a otros tratamientos, con total tranquilidad."
		},
		{
			icon: Activity,
			title: "Resultados que se notan",
			text: "Mejor digestión, sueño más profundo y piel más sana son algunos de los cambios que nuestros clientes reportan al hacerlos parte de su rutina diaria."
		},
		{
			icon: HeartPulse,
			title: "Protección a cualquier edad",
			text: "Fortalecen tus defensas y frenan el desgaste propio de la edad, acompañándote en cada etapa: desde la niñez y el embarazo hasta la adultez mayor."
		}
	] satisfies Feature[]
}

export const family = {
	title: "Para toda la familia",
	body: "No son fármacos ni productos naturistas: pueden ser ingeridos a cualquier edad, desde bebés, mujeres embarazadas o en lactancia, hasta personas mayores o quienes toman algún medicamento alterno.",
	// Imagen de fondo responsiva: una para móvil y otra para pantallas grandes.
	backgroundImage: {
		sm: imageBaseURL("para-toda-la-familia-sm.webp"),
		lg: imageBaseURL("para-toda-la-familia-lg.webp")
	}
}

const prodImg = (file: string): string => imageBaseURL(`products/${file}`)

/** Siete productos destacados en el acordeón de la home (imágenes placeholder en `public/images/products/`). */
const HOME_PRODUCT_SLUGS = [
	"shalosh-bayom",
	"agua-vita",
	"dermogel",
	"taftefet",
	"lazihum",
	"nazelet",
	"jabon-neutro"
] as const satisfies readonly ProductType[]

const HOME_PRODUCT_PLACEHOLDER_IMAGES: Record<(typeof HOME_PRODUCT_SLUGS)[number], string> = {
	"shalosh-bayom": "shalosh-1.webp",
	"agua-vita": "vita-1.webp",
	dermogel: "dermogel-1.webp",
	taftefet: "taftefet-3.webp",
	lazihum: "lazihum-3.webp",
	nazelet: "nazelet-1.webp",
	"jabon-neutro": "jabon-2.webp"
}

export const homeProductsCarousel: CarouselImageItem[] = HOME_PRODUCT_SLUGS.map(slug => {
	const product = PRODUCTS[slug]
	return {
		image: prodImg(HOME_PRODUCT_PLACEHOLDER_IMAGES[slug]),
		title: product.name,
		text: product.title,
		imageAlt: product.name,
		href: `/productos/${slug}`
	}
})

const imgTestimonial = (file: string): string => imageBaseURL(`testimonials/${file}`)

export const testimonials: Testimonial[] = [
	{
		name: "María González García",
		avatar: imgTestimonial("maria-gonzalez.jpg"),
		rating: 5,
		quote: "Después de meses con molestias, por fin siento alivio. Lo tomo a diario y mi energía cambió por completo."
	},
	{
		name: "Roberto Medrano Pérez",
		avatar: imgTestimonial("roberto-medrano.jpg"),
		rating: 4,
		quote: "Shalósh Bayóm ha mejorado mi control de la inflamación prostática."
	},
	{
		name: "Roberto Sánchez",
		avatar: imgTestimonial("roberto-sanchez.jpg"),
		rating: 5,
		quote: "A mis 68 años noto una diferencia real. Lo recomendé a toda mi familia sin dudarlo."
	},
	{
		name: "Lucía Hernández",
		avatar: imgTestimonial("lucia-hernandez.jpg"),
		rating: 5,
		quote: "Lo pude usar durante mi embarazo con total tranquilidad. Natural y sin efectos secundarios."
	},
	{
		name: "Angui Saldaña",
		avatar: imgTestimonial("angui-saldana.jpg"),
		rating: 5,
		quote: "Los consumo y mejoré muchísimo mi salud."
	},
	{
		name: "Lic. Carla Mendoza León",
		avatar: imgTestimonial("carla-mendoza.jpg"),
		rating: 4,
		quote: "Respiro mucho mejor y duermo de corrido. Sencillo de incorporar a la rutina diaria."
	},
	{
		name: "Ana Torres Legarreta",
		avatar: imgTestimonial("ana-torres.jpg"),
		rating: 4,
		quote: "Me siento más vital y descansada. Es parte de mi día como un vaso de agua más, quisiera que durara más tiempo."
	},
	{
		name: "Familia Ramírez",
		avatar: imgTestimonial("fam-ramirez.jpg"),
		rating: 5,
		quote: "Lo usamos todos en casa, hasta los niños. Tranquilidad de saber que es completamente natural."
	},
	{
		name: "Jorge Vázquez",
		avatar: imgTestimonial("jorge-vazquez.jpg"),
		rating: 5,
		quote: "Mi hijo tolera todo perfecto y lo toma feliz. El cambio en su bienestar fue notorio."
	},
	{
		name: "Patricia Flores Murillo",
		avatar: imgTestimonial("paty-flores.jpg"),
		rating: 5,
		quote: "Llevo un año usándolo y sigue funcionando con los mismos resultados. Lo recomiendo ampliamente."
	},
	{
		name: "María Valenzuela",
		avatar: imgTestimonial("maria-valenzuela.jpg"),
		rating: 5,
		quote: "Desde luego que los productos son muy buenos, yo tengo 20 años consumiéndolos y hasta los he recomendado porque son muy efectivos para diferentes enfermedades."
	},
	{
		name: "Moisés Pérez",
		avatar: imgTestimonial("moises-perez.jpg"),
		rating: 5,
		quote: "Con Lazihum desaparece de inmediato la irritación y escozor del salpullido y los piquetes de insectos, también mejora la irritación en la garganta."
	},
	{
		name: "Elizabeth Conde",
		avatar: imgTestimonial("elizabeth-conde.jpg"),
		rating: 5,
		quote: "Conozco el producto desde hace 12 años y por su utilización a nivel celular lo recomiendo ampliamente. Me encanta el gel para el acné, quemaduras y problemas en la piel."
	},
	{
		name: "Selene Laguna",
		avatar: imgTestimonial("selene-laguna.jpg"),
		rating: 5,
		quote: "Le puse una gota a mi hijo para la carnosidad y en tres horas su ojo quedó limpio completamente."
	},
	{
		name: "Sr. Fermín",
		avatar: imgTestimonial("sr-fermin.jpg"),
		rating: 4,
		quote: "Hoy comienzo la semana 4 de 5 y van disminuyendo los síntomas. Ya es poco el ardor de pies y pierna, el cosquilleo disminuye lentamente."
	},
	{
		name: "Moisés Pérez",
		avatar: imgTestimonial("moises-perez.jpg"),
		rating: 5,
		quote: "A mi alcance diré que he comprobado la efectividad del Dermogel, me desaparece las manchas rojizas en los brazos que me ocasiona el sol del mediodía y el calor."
	}
]
